import { AxiosError } from 'axios';
import { put, call, select, takeLatest } from 'redux-saga/effects';
import { selectors as clientSelectors } from 'store/clients';
import {
  types,
  Configuration,
  UpdateConfigurationRequest,
  CreateConfigurationRequest,
  DeleteConfigurationRequest
} from './types';
import { API } from 'services/api/base';
import { getErrorCode } from 'services/api/normalizer';

export function* getConfigurationsAsync() {
  yield put({ type: types.GET_CONFIGURATIONS.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/configurations`);
    const response: Configuration[] = yield call(api.getAll);

    yield put({ type: types.GET_CONFIGURATIONS.SUCCESS, payload: response });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.GET_CONFIGURATIONS.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.GET_CONFIGURATIONS.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.GET_CONFIGURATIONS.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.GET_CONFIGURATIONS.PENDING, payload: false });
  }
}

export function* createConfigurationAsync({ payload }: CreateConfigurationRequest) {
  yield put({ type: types.CREATE_CONFIGURATION.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/configurations`);
    yield call(api.create, payload);

    yield put({ type: types.CREATE_CONFIGURATION.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.CREATE_CONFIGURATION.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.CREATE_CONFIGURATION.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.CREATE_CONFIGURATION.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.CREATE_CONFIGURATION.PENDING, payload: false });
  }
}

export function* updateConfigurationAsync({ payload }: UpdateConfigurationRequest) {
  yield put({ type: types.UPDATE_CONFIGURATION.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/configurations`);
    const response: Configuration = yield call(api.update, payload);

    yield put({ type: types.UPDATE_CONFIGURATION.SUCCESS, payload: response });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.UPDATE_CONFIGURATION.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.UPDATE_CONFIGURATION.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.UPDATE_CONFIGURATION.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.UPDATE_CONFIGURATION.PENDING, payload: false });
  }
}

export function* deleteConfigurationAsync({ payload }: DeleteConfigurationRequest) {
  const { id } = payload;
  yield put({ type: types.DELETE_CONFIGURATION.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/configurations`);
    yield call(api.destroy, id);

    yield put({ type: types.DELETE_CONFIGURATION.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.DELETE_CONFIGURATION.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.DELETE_CONFIGURATION.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.DELETE_CONFIGURATION.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.DELETE_CONFIGURATION.PENDING, payload: false });
  }
}

export const sagas = [
  takeLatest(types.GET_CONFIGURATIONS.REQUEST, getConfigurationsAsync),
  takeLatest(types.CREATE_CONFIGURATION.REQUEST, createConfigurationAsync),
  takeLatest(types.UPDATE_CONFIGURATION.REQUEST, updateConfigurationAsync),
  takeLatest(types.DELETE_CONFIGURATION.REQUEST, deleteConfigurationAsync)
];
